import fetch from 'isomorphic-unfetch';

import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import assign from 'lodash/assign';
import isEqual from 'lodash/isEqual';
import pickBy from 'lodash/pickBy';

import { UiToast, UiModal } from 'components';
import User from './user';
import { formatTime } from './utils';
import track from './track';
import config, { DomainType } from './config';
import { create } from './debug';
// import browser from './browser';

const log = create('req');
const error = create('req:error');

const errorHandler = {
  401: (res, opt) => {
    console.error('未登录请求', opt.url);
    User.expired();
  },
  30405: (res, opt) => {
    console.error('未登录请求', opt.url);
    User.expired();
  },
  '30412': function () {
    User.showNoMoney();
  },
  30418: (res) => {
    if (res.unfreeze_time) {
      UiToast.info(
        `您因涉嫌违规而被系统冻结账号，将于${formatTime(
          new Date(res.unfreeze_time),
          'yyyy年MM月dd日',
        )}解封，如有问题请到 App 内申诉`,
        User.logout,
      );
    } else {
      UiToast.info('您因涉嫌违规而被系统冻结账号，如有问题请到 App 内申诉', User.logout);
    }
  },
  30421: () => {
    UiToast.fail('您的帐号已被冻结，请联系客服', () => {
      User.logout();
    });
  },
  30451: () => {
    UiToast.info('内容包含敏感词');
  },
  6805: (res) => {
    UiModal.confirm(
      '温馨提示',
      res.msg,
      () => {
        cryolite
          .post({
            url: '/api/v1/mystic/status',
            contentType: 'FORM_URL',
          })
          .then(() => {
            UiToast.success('已切换');
          })
          .catch((res) => {
            UiToast.fail(res.msg);
          });
      },
      () => {},
      '切回原身',
    );
  },
};
const errorMsg = {
  30405: 'Token 已失效',
  30412: '余额不足，请充值',
  30432: '手机号码错误',
  30431: '短信验证码无效',
  30433: '手机号码已绑定',
  30435: '短信验证间隔太短',
  30437: '短信验证码每日次数超过限制',
  // 30419: '发送短信需填写正确验证码',
  30250: '这是一个bug，反正我是信了',

  30301: '用户名或密码不正确',
  30302: '用户名或密码不正确',
  30307: '昵称重复',
  30308: '此邮箱已被注册',
  33010: '主播专属礼物，无法送给其他主播',
  30312: '用户名不存在',
  // 30405: '身份已经过期',
  // 30412: '余额不足，请充值',
  30413: '权限不足',
  30415: '房间已关闭直播',
  30416: '因您直播室出现违规行为，现在处于禁播状态，15分钟后方可正常直播',
  30417: '任务还未完成，无法领取柠檬',
  30418: '您的帐号已被冻结，请联系客服',
  30419: '验证码验证失败',
  30420: '沙发已经被捷足先登了，来！再战一次！',
  30421: '您的帐号已被冻结，请联系客服',
  30422: 'VIP特权，请购买VIP',
  30423: '注册过于频繁，请稍后注册',
  30424: '已被抢光，明天早点来',
  30428: '仅M特权专享',
  30444: '您还没有验证邮箱',
  30451: '包含敏感词',
  80001: '今日头像更换次数已达上限，请明日再试',
  80101: '今日修改昵称次数已达上限，请明日再试',
  97685: '设备暂时无法使用!',
  97686: '设备使用中，请稍后!',
  97684: '暂时不支持同时送多个喷喷!',
  97683: '主播已下线，敬请期待!',
};

const getErrorPromise = (msg) => {
  return new Promise((resolve, reject) => {
    error(msg);
    reject({
      code: 0,
      msg,
      message: msg,
    });
  });
};

// let {getToken}=_user;

const getFormData = (data) => {
  const formData = new FormData();
  forEach(data, (val, key) => {
    formData.append(key, val);
  });
  return formData;
};

type TrackOpt = {
  event: string;
  event_type: string;
  desc?: string;
};

// 接口URL类型
type UrlType =
  | 'api'
  | 'user'
  | 'pay'
  | 'flint'
  | 'cryolite'
  | 'vc'
  | 'amber'
  | 'mon'
  | 'game'
  | 'im'
  | 'hotspots';

// 请求方法
type MethodType = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD';

// 请求参数
type RequestOpt = {
  url: string;
  // 提交的数据
  data?: {
    [key: string]: any;
  };
  urlType?: UrlType;
  // 日志上报参数
  trackOpt?: TrackOpt;
  // 提交的数据类型
  contentType?: 'FORM_URL' | 'JSON';
  encode?: boolean;
  token?: boolean;
  disableCodeCheck?: boolean;
  timeout?: number;
  headers?: {};
  method?: MethodType;
  body?: any;
  mode?: RequestMode;
};

/**
 * ajax 请求
 * @param url {string}      url 地址（api 域下）
 * @param data {object}     数据参数
 * @param trackOpt {TrackOpt}     上报日志的参数
 * 或者
 * @param opt {object}
 * {
 *   urlType,{string}        url 的域名类型 默认值：api 详见 config.domain
 *   token,  {boolean}       是否需要token
 *   fail(res)               失败回调
 * }
 * @returns {Promise<*>}
 */
const request = (
  url: string | RequestOpt,
  data?: {
    [key: string]: any;
  },
  trackOpt?: TrackOpt,
) => {
  let _opt = url as RequestOpt;
  if (typeof url === 'string') {
    _opt = {
      url,
      data,
      trackOpt,
    } as RequestOpt;
  } else {
    trackOpt = _opt.trackOpt;
    delete _opt.trackOpt;
  }

  _opt = assign(_opt, {
    encode: true,
  });

  if (!_opt.timeout) {
    _opt.timeout = 1000 * 5;
  }

  const { urlType = 'api', token, ...opt } = _opt;

  if (!opt.url) {
    return getErrorPromise('request 缺少 url 参数');
  }

  if (!opt.headers) {
    opt.headers = {};
  }

  // opt.headers['platform'] = browser.memeClient ? 'pc_client' : 'pc_web';

  opt.method = (opt.method || 'GET').toUpperCase() as MethodType;
  if (urlType === 'cryolite') {
    // opt.credentials = 'include'
  }

  // 防止重复请求
  // let reqKey = `[${opt.method}]${opt.url}-${JSON.stringify(opt.data)}`;
  // if (cacheReq.get(reqKey)) {
  //     if (Date.now() - cacheReq.get(reqKey) < sameReqTimespan) {
  //         console.warn('请求太频繁', reqKey, opt.data)
  //         return new Promise((resolve, reject) => {
  //             error('请求太频繁');
  //             reject({
  //                 code: 0,
  //                 msg: '请求太频繁',
  //                 message: '请求太频繁'
  //             });
  //         });
  //     }
  // }
  //
  // cacheReq.set(reqKey, Date.now())
  //
  // if (cacheReq.size > 20) {
  //     let now = Date.now()
  //     cacheReq.forEach((value, key) => {
  //         if (now - value > sameReqTimespan) {
  //             cacheReq.delete(key)
  //         }
  //     })
  //     log('after clear cacheReq', cacheReq.size)
  // }

  opt.data = opt.data || {};
  const _getToken = () => {
    return opt.data!.access_token || User.getToken();
  };

  if (!User.getToken()) {
    if (opt.url.match(/\{(access_token|token)\}/gi) || opt.data.token || token) {
      log('need login url', opt.url);
      User.popupLogin();
      return getErrorPromise('未登录');
    }
  } else {
    opt.url = opt.url.replace(/\{(access_token|token)\}/gi, _getToken());
  }

  if (opt.data.token || token) {
    delete opt.data.token;
    opt.data.access_token = _getToken();
  }

  if (!/^http(s?):\/\//.test(opt.url)) {
    opt.url = `https://${config.domain![urlType]}/${opt.url.replace(/^\//, '').replace(/\/$/, '')}`;
  }

  if (opt.method === 'GET' && !isEmpty(opt.data)) {
    let paramStr = '';
    forEach(opt.data, (val, key) => {
      paramStr += `${key}=${opt.encode ? encodeURIComponent(val) : val}&`;
    });
    opt.url =
      opt.url +
      (opt.url.indexOf('?') > -1 ? '&' : '?') +
      (paramStr ? paramStr.slice(0, paramStr.length - 1) : '');
  }

  if (!opt.data._omitToken) {
    // 如果是登录用户 url 没有带token的 自动加上token参数
    if (!/access_token=\w{32}/.test(opt.url) && User.isLogin()) {
      opt.url += `${opt.url.indexOf('?') > -1 ? '&' : '?'}access_token=${_getToken()}`;
    }
  }

  delete opt.data._omitToken;

  if (opt.method === 'GET' || opt.method === 'HEAD') {
    delete opt.body;
  } else {
    // getFormData(opt.data);
  }

  if (opt.method === 'POST' || opt.method === 'PUT') {
    if (
      !opt.contentType &&
      ['pay', 'cryolite', 'mon', 'funny', 'game', 'vc', 'hotspots'].includes(urlType)
    ) {
      opt.contentType = 'JSON';
    }

    if (opt.contentType && opt.contentType.toUpperCase() === 'JSON') {
      opt.headers['Content-Type'] = 'application/json';
      opt.body = JSON.stringify(opt.data);
    } else {
      opt.body = getFormData(opt.data);
    }
    // opt.headers = {'Content-Type': 'application/x-www-form-urlencoded'}
  }

  // log('');
  // log(`[request][${urlType}][${opt.method}]`);
  // log('=>', opt.url, opt.data);
  // log('');

  const trackFn = (isSuccess, res, req_time) => {
    // console.log('trackOpt', trackOpt, opt.url)
    const { message, ...req_res } = res;
    const { body, timeout, url, ...req_opt } = opt;
    if (trackOpt || (req_time > 3000 && User.isStar())) {
      track.send(
        assign(
          {
            event: 'req',
            event_type: `req_${isSuccess ? 'ok' : 'err'}`,
          },
          trackOpt,
          {
            content: trackOpt ? req_res : '',
            req_type: isSuccess ? 'ok' : 'err',
            req_url: url,
            req_path: url.split('?')[0],
            req_opt,
            req_time,
          },
        ),
      );
    } else if (!isSuccess) {
      track.send({
        event: 'req',
        event_type: 'req_err',
        content: req_res,
        req_type: 'err',
        req_url: url,
        req_path: url.split('?')[0],
        req_opt,
        req_time,
      });
    }
  };

  const stime = Date.now();
  return fetch(opt.url, opt)
    .then((res) => {
      // log("[response error11]", opt.url, res)
      // let response = await res.text()
      // log("[response]", opt.url, res.ok, res.status, res.statusText,response.length)
      // if(response.length){
      //   return JSON.parse(response)
      // }
      // return {}
      if (res.type === 'opaque' && res.status === 0) {
        return {};
      }
      try {
        return res.json();
      } catch (e) {
        throw new Error(`url ${opt.url} res.json() fail`);
      }
    })
    .then((res: any) => {
      // log('[response]', opt.url)
      // log('==>', res)
      // try {

      const timespan = Date.now() - stime;
      if (timespan > 2 * 1000) {
        error('请求时间超过 2s');
      }

      return new Promise((resolve, reject) => {
        const { code } = res;
        if (urlType === 'vc') {
          res = res.data;
        }
        if (typeof code === 'undefined') {
          res = {
            code: 1,
            data: res,
          };
        }

        if (typeof res.data === 'object' && !Array.isArray(res.data)) {
          res.data = pickBy(res.data, (item) => {
            return item !== null;
          });
        }
        // 不进行 code 级别的错误校验
        if (opt.disableCodeCheck) {
          trackFn(true, res, timespan);
          resolve(res);
          return;
        }

        if (res.code === 1) {
          trackFn(true, res, timespan);
          resolve(res);
          // reject(res)
        } else {
          res.msg = errorMsg[res.code] || res.msg || res.message || `[${res.code}]请求数据失败`;

          error('[response error]', opt.url, res);
          if (errorHandler[code]) {
            errorHandler[code](res, opt);
            trackFn(false, res, timespan);
            // reject(null);
          } else {
            trackFn(false, res, timespan);
            reject(res);
          }
        }
      });
      // } catch (e){
      //     return e
      // }
    })
    .catch((res) => {
      error('url', opt.url, res);

      // opt.fail(res);
      // error('err=========>', res)
      return Promise.reject({
        ...res,
        code: res?.code ?? -100,
        msg: res?.msg || '请求异常',
        url: opt.url,
      });
    });
};

const getRequestType = (method: MethodType, urlType: UrlType) => {
  return (url: string | RequestOpt, data?, trackOpt?: TrackOpt) => {
    let opt = url as RequestOpt;
    if (typeof url === 'string') {
      opt = {
        url,
        data,
        trackOpt,
      };
    }
    return request({ ...opt, urlType, method });
  };
};

const getRequests = (urlType) => {
  return {
    get: getRequestType('GET', urlType),
    post: getRequestType('POST', urlType),
    delete: getRequestType('DELETE', urlType),
    put: getRequestType('PUT', urlType),
    patch: getRequestType('PATCH', urlType),
  };
};

const api = getRequests('api');
const user = getRequests('user');
const pay = getRequests('pay');
const flint = getRequests('flint');
const cryolite = getRequests('cryolite');
const vc = getRequests('vc');
const amber = getRequests('amber');
const mon = getRequests('mon');
const funny = getRequests('funny');
const game = getRequests('game');
const hotspots = getRequests('hotspots');
const im = getRequests('im');

const get = getRequestType('GET', 'api');
const post = getRequestType('POST', 'api');
const del = getRequestType('DELETE', 'api');
const put = getRequestType('PUT', 'api');
const patch = getRequestType('PATCH', 'api');

export {
  api,
  user,
  pay,
  flint,
  cryolite,
  vc,
  amber,
  mon,
  funny,
  hotspots,
  game,
  get,
  del,
  post,
  put,
  patch,
  im,
};

export default request;
